import React, { useEffect } from 'react';
import { Route, Switch, useRouteMatch, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Div, Typography } from '@icecreamsocial/components';
import {
	FacebookButton,
	ShareLink,
	Offer,
	MessengerButton,
	OfferExplanation,
	PageWrapper,
	SmsButton,
	EmailButton,
} from '../../../../presentational';
import { Email } from './Email';
import { useStore } from '../../../../hooks';
import { deepLinks } from '../../../../../utils';
import { useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

/**
 * @feature-flags
 */
const ENABLE_EMAIL_SHARE =
	process.env.ENABLE_EXPERIMENTAL_EMAIL_SHARE === 'true';

export const ShareOptions = observer(({ ...rest }) => {
	/**
	 * initialize hooks
	 */
	const intl = useIntl();
	const { path } = useRouteMatch();
	const history = useHistory();
	const { Campaign, Influencer, shareMessage, shareTitle } = useStore();
	const { enqueueSnackbar } = useSnackbar();

	/**
	 * some constants
	 */
	const { messenger, sms, defaultEmailClient } = deepLinks({
		link: Influencer?.inviteLink,
		message: shareMessage,
		subject: shareTitle,
	});

	const ROOT_PATH = path;
	const EMAIL_PATH = `${path}/email`;

	/**
	 * localization strings
	 */
	const LINK_LABEL = intl.formatMessage({
		id: 'share.general.linkLabel',
		defaultMessage: 'Use your personal link to share anywhere',
		description: 'Label for a copyable, personal link for the user',
	});

	const COPY_SUCCESS = intl.formatMessage({
		id: 'share.general.copySuccess',
		defaultMessage: '✨ Link copied!',
		description:
			'Success popup that appears when a user clicks on the copy button.  This message includes a star emoji (✨) for style and can be kept, omitted, or changed for translation.',
	});

	const SHARE_OPTIONS = intl.formatMessage({
		id: 'share.general.otherOptions',
		defaultMessage: 'Or share now on these platforms',
		description: 'Text that appears above buttons for other sharing methods',
	});

	// @ts-ignore
	const HELP = intl.formatMessage(
		{
			id: 'share.general.help',
			defaultMessage: `<bold>Having trouble?</bold> Apps might not open if not installed on your device, try using <bold2>Share</bold2> by your personal link instead`,
			description:
				'Help text that directs the user to try sharing using other methods if they are having trouble.  Please do not translate <bold>, </bold>, <bold2>, or </bold2>.  The words in between these will be bolded for emphasis.  The word "Share" between <bold2> refers directly to the "Share" button next to personal link which appears on certain devices',
		},
		{
			bold: (str) => <Typography.Span typoWeight='bold'>{str}</Typography.Span>,
			bold2: (str) => (
				<Typography.Span typoWeight='bold'>{str}</Typography.Span>
			),
		}
	);

	useEffect(() => {
		// 👇️ scroll to top on page load
		window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
	}, []);

	return (
		<>
			<PageWrapper>
				<Switch>
					<Route path={ROOT_PATH} exact>
						<Offer {...rest} />
						<ShareLink
							id='icsShareLink'
							label={LINK_LABEL}
							link={Influencer?.inviteLink}
							title={Campaign?.shareTitle}
							text={shareMessage}
							marginBottom='lg'
							onCopy={() =>
								enqueueSnackbar(COPY_SUCCESS, {
									variant: 'info',
									autoHideDuration: 2500,
								})
							}
						/>
						<Typography.Span typoWeight='400' display='block' marginBottom='sm'>
							{SHARE_OPTIONS}
						</Typography.Span>
						<Div
							grid
							gridColumns={['repeat(2, 1fr)']}
							style={{ gap: '8px' }}
							marginBottom='lg'
						>
							<FacebookButton
								appId={process.env.FB_APP_ID}
								version={process.env.FB_SDK_VERSION}
								link={Influencer?.inviteLink}
							/>
							<MessengerButton onClick={messenger} />
							<EmailButton
								onClick={defaultEmailClient}
								link={Influencer?.inviteLink}
							/>
							{ENABLE_EMAIL_SHARE && (
								<EmailButton
									as={Typography.A}
									href={mailToLink}
									link={Influencer?.inviteLink}
								/>
							)}
							<SmsButton onClick={sms} />
						</Div>
						<Typography.P>{HELP}</Typography.P>
					</Route>
					{ENABLE_EMAIL_SHARE && (
						<Route path={EMAIL_PATH} exact>
							<Email />
						</Route>
					)}
				</Switch>
			</PageWrapper>
			{/* <OfferExplanation /> */}
		</>
	);
});
